import { computed, nextTick, onMounted, watch } from 'vue'

import { useConfigStore } from '../stores/config'
import { usePageStore } from '../stores/page'
import { useCdnImages } from './use-cdn-images'
import { useCoverImage } from './use-cover-image'
import { useImgSrcSet } from './use-img-src-set'
import { shouldPreloadTeaserImage } from '../utils/image-priority'

import type { ComposedParagraphTeaser } from '@xcel-next/composer-schema'

// `affiliate` is assigned on manual teasers (see modules/composer-util/src/util/teaser-util.ts)
// but is missing from ComposedParagraphTeaserSchema - added here until the schema is fixed.
export type TeaserObject = ComposedParagraphTeaser & { affiliate?: boolean }

export type TeaserMetaData = {
  brand?: string,
  teaserGroupType?: string,
  isSliderPreview?: boolean,
  isLoading?: boolean,
  error?: boolean,
  errorNotEnoughContent?: boolean,
  imgUrlLoading?: string,
  imgUrlError?: string,
  imgUrlErrorNotEnoughContent?: string,
  isEpgCard?: boolean
}

export type TeaserProps = {
  composedTeaser: TeaserObject,
  metaData?: TeaserMetaData,
  includeDepth?: number,
  listOrderPosition?: number,
  isInlineTeaser?: boolean,
  dataSliderInitialSlide?: boolean,
  prioritizeImage?: boolean
}

type TeaserPreloadLink = {
  rel: 'preload',
  href: string,
  as: 'image',
  fetchpriority: 'high',
  media: string,
  vmid: string
}

export function useTeaser (props: TeaserProps) {
  const configStore = useConfigStore()
  const pageStore = usePageStore()
  const { createImgSrc, createImgSrcSetArray } = useImgSrcSet()
  const { cdnImageUrl } = useCdnImages()
  const { extractMagazineName, coverImageUrl } = useCoverImage()

  const metaData = computed(() => props.metaData ?? {})
  const brand = computed(() => props.composedTeaser.ownerBrand || metaData.value.brand)

  const showInlineTeaserSliderStyle = computed(() => !!(
    configStore.rsConfig.teaserSettings?.enableInlineTeaserSliderStyleForGalleries &&
    props.isInlineTeaser &&
    props.composedTeaser.hasSlider &&
    props.composedTeaser.numSlides &&
    props.composedTeaser.sliderContents
  ))

  const setBrandBadgeAlt = computed(() =>
    props.composedTeaser.ownerBrand
      ? props.composedTeaser.ownerBrand.charAt(0).toUpperCase() + props.composedTeaser.ownerBrand.substring(1)
      : ''
  )

  const setLinkTarget = computed(() =>
    props.composedTeaser.target
      ? props.composedTeaser.target
      : props.composedTeaser.ownerBrand ? '_blank' : '_self'
  )

  const showDownloadIcon = computed(() =>
    !!(props.composedTeaser.hasDownload && configStore.rsConfig.teaserSettings?.shouldShowDownloadIconOnTeaser)
  )

  const showPlayIcon = computed(() =>
    (props.composedTeaser.articleType === 'video' || !!props.composedTeaser.hasAutoPlay) &&
    metaData.value.teaserGroupType !== 'bx-teaser-container--info'
  )

  const showGalleryIcon = computed(() => props.composedTeaser.articleType === 'gallery')

  const showAdBadge = computed(() =>
    !!((props.composedTeaser.advertorial && !props.composedTeaser.hideAdvertorial) || props.composedTeaser.affiliate)
  )

  const showOwnerBrand = computed(() => !!props.composedTeaser.ownerBrand)

  const imageIsAiGenerated = computed(() => !!props.composedTeaser.imageIsAiGenerated)

  const setStyleClasses = computed(() => ({
    'bx-teaser': true,
    'bx-teaser--video': showPlayIcon.value,
    'bx-teaser--favorite': isFavoriteTeaser.value,
    'bx-typo--teaser': true
  }))

  const headline = computed(() => props.composedTeaser.titleLong || props.composedTeaser.titleShort)

  const imageAltText = computed(() =>
    props.composedTeaser.imageAltText || props.composedTeaser.titleLong || props.composedTeaser.titleShort
  )

  const copytext = computed(() => props.composedTeaser.textLong || props.composedTeaser.textShort)

  // e.g. sweepstakes: a plain url outside the image service, so no srcset and no croppings
  const externalImageSrc = computed(() => props.composedTeaser.imageId ? undefined : props.composedTeaser.externalImageSrc)

  const imageUrl = computed(() => createImgSrc({
    image: props.composedTeaser,
    brand: brand.value ?? '',
    setName: metaData.value.teaserGroupType
  }) ?? externalImageSrc.value ?? null)

  const imageFetchPriority = computed(() => props.prioritizeImage ? 'high' : undefined)

  const isEditorialContext = computed(() =>
    pageStore.isArticleOrArticleVersion ||
    !!(props.composedTeaser.advertorial && !props.composedTeaser.hideAdvertorial) ||
    !!props.isInlineTeaser
  )

  const headlineTagType = computed(() =>
    isEditorialContext.value ? 'div' : metaData.value.teaserGroupType === 'bx-teaser-container--info' ? 'h2' : 'h3'
  )

  const shouldAddDataNoSnippet = computed(() => (isEditorialContext.value ? '' : null))

  const isFavoriteTeaser = computed(() =>
    !!props.composedTeaser?.advantages?.length
  )

  const imageSourceSetName = computed(() =>
    isFavoriteTeaser.value ? 'bx-teaser-container--recipe-favorite' : metaData.value.teaserGroupType
  )

  // derived from the current props only, a previous (external or wider) image must never stick to a reused component
  const fallbackPlaceholder = computed(() => {
    if (externalImageSrc.value) return { src: externalImageSrc.value, width: 0 }

    return createImgSrcSetArray(props.composedTeaser, brand.value ?? '', imageSourceSetName.value, 'webp')
      .reduce<{ src?: string, width: number }>((widest, { src }) => {
        const width = Number(src.match(/w=([0-9]+)/)?.[1] ?? 0)
        return width > widest.width ? { src: src.replace('.jpeg', '.webp'), width } : widest
      }, { width: 0 })
  })
  const fallbackPlaceholderSource = computed(() => fallbackPlaceholder.value.src)
  const fallbackPlaceholderSourceWidth = computed(() => fallbackPlaceholder.value.width)

  function setImageOverlayBackgroundSource (teaser: TeaserObject) {
    const overlayDiv = document.getElementsByClassName('bx-js-image-overlay-' + teaser.imageId)
    if (overlayDiv.length) {
      const magazine = extractMagazineName(teaser.overlayImageUrl as string)
      const availableHeight = (overlayDiv[0] as HTMLElement).clientHeight
      // base width (2:3 Ratio)
      const rawWidth = availableHeight * (2 / 3)
      // round up to next multiple of 20
      const optimalWidth = Math.ceil(rawWidth / 20) * 20
      ;(overlayDiv[0] as HTMLElement).style.backgroundImage = `url(${coverImageUrl(magazine, optimalWidth)})`
    }
  }

  function getImageOverlayStyleClass (teaser: TeaserObject) {
    return 'bx-teaser__image-overlay bx-js-image-overlay-' + teaser.imageId
  }

  function imgSrcSetArray (type: string) {
    // external images have no image service sources, TeaserPicture's <img> shows the fallback instead
    if (externalImageSrc.value) return []

    return createImgSrcSetArray(props.composedTeaser, brand.value ?? '', imageSourceSetName.value, type)
  }

  function setMediaQuery (viewport: number | string) {
    return '(max-width: ' + viewport + 'px)'
  }

  function setMediaQueryPreload (minViewport: number | string, maxViewport: number | string) {
    return '(min-width: ' + minViewport + '.1px) and (max-width: ' + maxViewport + 'px)'
  }

  function brandBadgeSrc (type: string) {
    // isPreview property is set in TeaserGroupTeaser component in theGUI within the metaData property
    return cdnImageUrl(`/images/${props.composedTeaser.ownerBrand}/${type}-badge.png`)
  }

  function addTeaserImagePreloadLinksToHead () {
    // add preload link for lead image srcset - webp only
    const meta: { link?: TeaserPreloadLink[] } = {}
    if (imageUrl.value && shouldPreloadTeaserImage(props.dataSliderInitialSlide, props.listOrderPosition, props.prioritizeImage)) {
      const sourceArray = imgSrcSetArray('webp')
      meta.link = []
      sourceArray.forEach((singleSrc, index) => {
        meta.link!.push({
          rel: 'preload',
          href: singleSrc.src,
          as: 'image',
          fetchpriority: 'high',
          media: setMediaQueryPreload(index > 0 ? sourceArray[index - 1].viewportWidth : 0, singleSrc.viewportWidth),
          vmid: 'img-preload-' + singleSrc.viewportWidth
        })
      })
    }
    return meta
  }

  watch(() => props.composedTeaser, async () => { // this is needed for the preview
    if (!import.meta.env.SSR && props.composedTeaser.overlayImageUrl && props.composedTeaser.overlayImageUrl.length > 0) {
      await nextTick()
      setImageOverlayBackgroundSource(props.composedTeaser)
    }
  })

  onMounted(() => {
    if (!import.meta.env.SSR && props.composedTeaser.overlayImageUrl && props.composedTeaser.overlayImageUrl.length > 0) {
      setImageOverlayBackgroundSource(props.composedTeaser)
    }
  })

  return {
    fallbackPlaceholderSource,
    fallbackPlaceholderSourceWidth,
    showInlineTeaserSliderStyle,
    setBrandBadgeAlt,
    setLinkTarget,
    showDownloadIcon,
    showPlayIcon,
    showGalleryIcon,
    showAdBadge,
    showOwnerBrand,
    imageIsAiGenerated,
    imageSourceSetName,
    setStyleClasses,
    headline,
    imageAltText,
    copytext,
    imageUrl,
    imageFetchPriority,
    headlineTagType,
    shouldAddDataNoSnippet,
    isFavoriteTeaser,
    isEditorialContext,
    cdnImageUrl,
    getImageOverlayStyleClass,
    imgSrcSetArray,
    setMediaQuery,
    setMediaQueryPreload,
    brandBadgeSrc,
    addTeaserImagePreloadLinksToHead
  }
}
